import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  TablePagination, TableSortLabel, MenuItem, Chip
} from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const emptyForm = {
  Name: '',
  Code: '',
  Description: '',
  Website: '',
  Email: '',
  UserEmail: '',
  PhoneNo: '',
  CompanyLogo: '',
};

function Organizations() {
  const [organizations, setOrganizations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('Name');
  const [order, setOrder] = useState('asc');

  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    org: null,
    subscriptions: [],
    loading: false,
  });
  const [subForm, setSubForm] = useState({
    firstName: '',
    lastName: '',
    mobileNo: '',
    email: '',
    plan: '',
    status: 'active',
    endDate: '',
  });
  const [editingSub, setEditingSub] = useState(null);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/organizations');
      setOrganizations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await apiGet('/api/subscription-plans');
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  useEffect(() => {
    fetchOrganizations();
    fetchPlans();
  }, []);

  const handleOpenDialog = (org = null) => {
    setEditOrg(org);
    setForm(org ? {
      Name: org.Name || '',
      Code: org.Code || '',
      Description: org.Description || '',
      Website: org.Website || '',
      Email: org.Email || '',
      UserEmail: org.UserEmail || '',
      PhoneNo: org.PhoneNo || '',
      CompanyLogo: org.CompanyLogo || '',
    } : emptyForm);
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => setOpenDialog(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      let res;
      if (editOrg) {
        res = await apiPut(`/api/organizations/${editOrg._id}`, form);
        if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Update failed');
        setSuccess('Organization updated');
      } else {
        res = await apiPost('/api/organizations', form);
        if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Create failed');
        setSuccess('Organization created');
      }
      handleCloseDialog();
      await fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async (org) => {
    const confirmed = window.confirm(
      `Delete organization "${org.Name}"?\n\nThis will permanently remove:\n- Related subscriptions/history\n- Organization database ${`CRM_${org.Code}`}\n- Organization record`
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      const res = await apiDelete(`/api/organizations/${org._id}`);
      if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Delete failed');
      const dbInfo = res.data?.database;
      setSuccess(
        `Organization deleted. Removed ${res.data?.deletedSubscriptions || 0} subscription(s)` +
        (dbInfo?.dropped ? `, dropped database ${dbInfo.dbName}` : dbInfo?.reason ? ` (DB drop skipped: ${dbInfo.reason})` : '')
      );
      await fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleOpenDetails = async (org) => {
    setDetailsDialog({ open: true, org, subscriptions: [], loading: true });
    setEditingSub(null);
    try {
      const res = await apiGet(`/api/organizations/${org._id}`);
      setDetailsDialog({
        open: true,
        org: res.data,
        subscriptions: res.data?.subscriptions || [],
        loading: false,
      });
    } catch (err) {
      setDetailsDialog({ open: true, org, subscriptions: [], loading: false });
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialog({ open: false, org: null, subscriptions: [], loading: false });
    setEditingSub(null);
  };

  const handleEditSubscription = (sub) => {
    setEditingSub(sub);
    setSubForm({
      firstName: sub.firstName || '',
      lastName: sub.lastName || '',
      mobileNo: sub.mobileNo || '',
      email: sub.email || '',
      plan: sub.plan?._id || sub.plan || '',
      status: sub.status || 'active',
      endDate: sub.endDate ? String(sub.endDate).slice(0, 10) : '',
    });
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!detailsDialog.org || !editingSub) return;
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...subForm,
        endDate: subForm.endDate || undefined,
      };
      const res = await apiPut(
        `/api/organizations/${detailsDialog.org._id}/subscriptions/${editingSub._id}`,
        payload
      );
      if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Subscription update failed');
      setSuccess('Subscription updated');
      setEditingSub(null);
      await handleOpenDetails(detailsDialog.org);
      await fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  function sortComparator(a, b) {
    const valA = (a[orderBy] || '').toString().toLowerCase();
    const valB = (b[orderBy] || '').toString().toLowerCase();
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  }

  const filtered = organizations
    .filter((org) => {
      const q = search.toLowerCase();
      return (
        (org.Name || '').toLowerCase().includes(q) ||
        (org.Code || '').toLowerCase().includes(q) ||
        (org.Email || '').toLowerCase().includes(q) ||
        (org.PhoneNo || '').toLowerCase().includes(q)
      );
    })
    .sort(sortComparator);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>Organizations</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search organizations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Organization
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'Name' ? order : false}>
                <TableSortLabel active={orderBy === 'Name'} direction={orderBy === 'Name' ? order : 'asc'} onClick={() => handleSort('Name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'Code' ? order : false}>
                <TableSortLabel active={orderBy === 'Code'} direction={orderBy === 'Code' ? order : 'asc'} onClick={() => handleSort('Code')}>
                  Code
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Website</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No organizations found</TableCell>
              </TableRow>
            )}
            {paginated.map((org) => (
              <TableRow key={org._id} hover>
                <TableCell>{org.Name}</TableCell>
                <TableCell><Chip size="small" label={org.Code} /></TableCell>
                <TableCell>{org.Email || '-'}</TableCell>
                <TableCell>{org.PhoneNo || '-'}</TableCell>
                <TableCell>{org.Website || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Manage">
                    <IconButton onClick={() => handleOpenDetails(org)}><Visibility /></IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleOpenDialog(org)}><Edit /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete organization + related data/database">
                    <IconButton color="error" onClick={() => handleDelete(org)}><Delete /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editOrg ? 'Edit Organization' : 'Add Organization'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField label="Name" name="Name" value={form.Name} onChange={handleChange} fullWidth margin="normal" required />
            <TextField label="Code" name="Code" value={form.Code} onChange={handleChange} fullWidth margin="normal" required helperText="Used for related database name (CRM_<Code>)" />
            <TextField label="Description" name="Description" value={form.Description} onChange={handleChange} fullWidth margin="normal" multiline minRows={2} />
            <TextField label="Website" name="Website" value={form.Website} onChange={handleChange} fullWidth margin="normal" />
            <TextField label="Email" name="Email" value={form.Email} onChange={handleChange} fullWidth margin="normal" />
            <TextField label="User Email" name="UserEmail" value={form.UserEmail} onChange={handleChange} fullWidth margin="normal" />
            <TextField label="Phone No" name="PhoneNo" value={form.PhoneNo} onChange={handleChange} fullWidth margin="normal" />
            <TextField label="Company Logo URL" name="CompanyLogo" value={form.CompanyLogo} onChange={handleChange} fullWidth margin="normal" />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">{editOrg ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={detailsDialog.open} onClose={handleCloseDetails} fullWidth maxWidth="md">
        <DialogTitle>
          Manage Organization {detailsDialog.org ? `- ${detailsDialog.org.Name}` : ''}
        </DialogTitle>
        <DialogContent>
          {detailsDialog.loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1"><strong>Code:</strong> {detailsDialog.org?.Code}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {detailsDialog.org?.Email || '-'}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {detailsDialog.org?.PhoneNo || '-'}</Typography>
                <Typography variant="body2"><strong>Website:</strong> {detailsDialog.org?.Website || '-'}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {detailsDialog.org?.Description || '-'}</Typography>
              </Box>

              <Typography variant="h6" mb={1}>Subscriptions</Typography>
              {detailsDialog.subscriptions.length === 0 ? (
                <Typography color="text.secondary" mb={2}>No subscriptions linked to this organization.</Typography>
              ) : (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailsDialog.subscriptions.map((sub) => (
                      <TableRow key={sub._id}>
                        <TableCell>{sub.firstName} {sub.lastName}</TableCell>
                        <TableCell>{sub.email}</TableCell>
                        <TableCell>{sub.plan?.name || '-'}</TableCell>
                        <TableCell>{sub.status}</TableCell>
                        <TableCell>{sub.endDate ? String(sub.endDate).slice(0, 10) : '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleEditSubscription(sub)}>Update</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {editingSub && (
                <Box component="form" onSubmit={handleUpdateSubscription} sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" mb={1}>Update Subscription</Typography>
                  <TextField label="First Name" name="firstName" value={subForm.firstName} onChange={(e) => setSubForm({ ...subForm, firstName: e.target.value })} fullWidth margin="dense" required />
                  <TextField label="Last Name" name="lastName" value={subForm.lastName} onChange={(e) => setSubForm({ ...subForm, lastName: e.target.value })} fullWidth margin="dense" required />
                  <TextField label="Mobile No" name="mobileNo" value={subForm.mobileNo} onChange={(e) => setSubForm({ ...subForm, mobileNo: e.target.value })} fullWidth margin="dense" required />
                  <TextField label="Email" name="email" value={subForm.email} onChange={(e) => setSubForm({ ...subForm, email: e.target.value })} fullWidth margin="dense" required />
                  <TextField
                    label="Plan"
                    name="plan"
                    select
                    value={subForm.plan}
                    onChange={(e) => setSubForm({ ...subForm, plan: e.target.value })}
                    fullWidth
                    margin="dense"
                    required
                  >
                    {plans.map((plan) => (
                      <MenuItem key={plan._id} value={plan._id}>{plan.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Status"
                    name="status"
                    select
                    value={subForm.status}
                    onChange={(e) => setSubForm({ ...subForm, status: e.target.value })}
                    fullWidth
                    margin="dense"
                    required
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                  <TextField
                    label="End Date"
                    name="endDate"
                    type="date"
                    value={subForm.endDate}
                    onChange={(e) => setSubForm({ ...subForm, endDate: e.target.value })}
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button type="submit" variant="contained">Save Subscription</Button>
                    <Button onClick={() => setEditingSub(null)}>Cancel</Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Organizations;
